import { Star } from "lucide-react";
import { useRef } from "react";
import { cn } from "@/lib/utils";

const STARS = [1, 2, 3] as const;

interface StarRatingProps {
  value: 0 | 1 | 2 | 3;
  onChange: (value: 0 | 1 | 2 | 3) => void;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}

/**
 * A 3-star rating component.
 *
 * Behaviour:
 * - Click a star that is NOT yet active → set that value (1-3).
 * - Click the currently active star → reset to 0 (un-rate).
 * - Keyboard: when the container has focus, keys 0-3 set the value directly.
 * - Accessible: aria-label + individual aria-pressed on each button.
 */
export function StarRating({
  value,
  onChange,
  disabled = false,
  size = "md",
  className,
}: StarRatingProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const iconSize = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  function handleStarClick(star: 1 | 2 | 3) {
    if (disabled) return;
    // Toggle off if already selected
    onChange(value === star ? 0 : star);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    const key = e.key;
    if (key === "0") {
      e.preventDefault();
      onChange(0);
    } else if (key === "1" || key === "2" || key === "3") {
      e.preventDefault();
      onChange(Number(key) as 1 | 2 | 3);
    }
  }

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={`Priorità: ${value === 0 ? "non valutata" : `${value} su 3`}`}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      className={cn(
        "inline-flex items-center gap-0.5 outline-none",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
    >
      {STARS.map((star) => {
        const filled = star <= value;
        return (
          <button
            key={star}
            type="button"
            disabled={disabled}
            aria-label={`${star} stella${star > 1 ? " stelle" : ""}`}
            aria-pressed={star === value}
            onClick={() => handleStarClick(star)}
            className={cn(
              "transition-all focus:outline-none",
              disabled
                ? "cursor-not-allowed"
                : "cursor-pointer hover:scale-110",
              filled
                ? "text-amber-400"
                : "text-muted-foreground/40 hover:text-amber-300",
            )}
          >
            <Star
              className={cn(iconSize, filled ? "fill-amber-400" : "fill-transparent")}
              strokeWidth={filled ? 0 : 1.5}
            />
          </button>
        );
      })}
    </div>
  );
}
